import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SuperAdminAuthProvider, useSuperAdminAuth } from './context/SuperAdminAuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import MonProfil from './pages/profil/MonProfil';
import Produits from './pages/produits/Produits';
import Stock from './pages/stock/Stock';
import Fournisseurs from './pages/fournisseurs/Fournisseurs';
import BonsCommande from './pages/bons-commande/BonsCommande';
import Vendeurs from './pages/vendeurs/Vendeurs';
import VentesMagasin from './pages/ventes/VentesMagasin';
import Commandes from './pages/commandes/Commandes';
import Caisse from './pages/caisse/Caisse';
import Rapports from './pages/rapports/Rapports';
import JournalVentes from './pages/rapports/JournalVentes';
import Parametres from './pages/parametres/Parametres';
import ActiviteCompte from './pages/parametres/ActiviteCompte';
import MonAbonnement from './pages/parametres/MonAbonnement';
// Modules métier L11–L17
import Devis from './pages/devis/Devis';
import RetoursClients from './pages/retours-clients/RetoursClients';
import Inventaires from './pages/inventaires/Inventaires';
import PrixPromotions from './pages/prix/PrixPromotions';
import Taxes from './pages/taxes/Taxes';
import ChangerMotDePasseForce from './pages/auth/ChangerMotDePasseForce';
import ResetPassword from './pages/auth/ResetPassword';
import PWAInstaller from './components/pwa/PWAInstaller';
import NouveautesAuto from './components/pwa/NouveautesAuto';

// Lot L04 — Console Super Admin (URL séparée /superadmin/*)
import SuperAdminLogin from './pages/superadmin/Login';
import SuperAdminResetPassword from './pages/superadmin/ResetPassword';
import SuperAdminLayout from './pages/superadmin/Layout';
import SuperAdminCommerces from './pages/superadmin/Commerces';
import SuperAdminCommerceDetail from './pages/superadmin/CommerceDetail';
import SuperAdminPlans from './pages/superadmin/Plans';
// Lots L22-L23 — sauvegardes + audit
import SuperAdminSauvegardes from './pages/superadmin/Sauvegardes';
import SuperAdminAuditLogs from './pages/superadmin/AuditLogs';
import SuperAdminMonProfil from './pages/superadmin/MonProfil';
import SuperAdminPresence from './pages/superadmin/Presence';
import SuperAdminGestionSA from './pages/superadmin/SuperAdmins';

// ─────────────────────────────────────────────────────────────────────
// ProtectedLayout pour les routes USER
// Si l'utilisateur a un mot de passe temporaire (créé/réinitialisé par
// admin), on l'oblige à passer par /changer-mot-de-passe avant tout.
// ─────────────────────────────────────────────────────────────────────
const ProtectedLayout = ({ roles }) => {
  const { user, loading, mustChangePassword } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-2 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/changer-mot-de-passe" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

// ─────────────────────────────────────────────────────────────────────
// ProtectedLayout pour les routes SUPER ADMIN
// Branche isolée : son propre AuthProvider, son propre token (sv_sa_*).
// ─────────────────────────────────────────────────────────────────────
const ProtectedSuperAdminLayout = () => {
  const { superAdmin, loading } = useSuperAdminAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!superAdmin) return <Navigate to="/superadmin/login" replace />;
  return <SuperAdminLayout />;
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Changement de mot de passe forcé — accessible uniquement si connecté
          ET si le flag doit_changer_mot_de_passe est à TRUE */}
      <Route
        path="/changer-mot-de-passe"
        element={
          !user
            ? <Navigate to="/login" replace />
            : user.doit_changer_mot_de_passe
              ? <ChangerMotDePasseForce />
              : <Navigate to="/dashboard" replace />
        }
      />

      {/* Routes accessibles à tous les rôles connectés */}
      <Route element={<ProtectedLayout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="mon-profil" element={<MonProfil />} />
        <Route path="produits" element={<Produits />} />
        <Route path="stock" element={<Stock />} />
        <Route path="vendeurs/*" element={<Vendeurs />} />
        <Route path="ventes" element={<VentesMagasin />} />
        <Route path="commandes" element={<Commandes />} />
        {/* Modules métier — visibles à tous les rôles staff */}
        <Route path="devis" element={<Devis />} />
        <Route path="retours-clients" element={<RetoursClients />} />
        <Route path="inventaires" element={<Inventaires />} />
      </Route>

      {/* Catalogue + approvisionnement — admin/propriétaire/GÉRANT + consultation.
          Le gérant peut passer commande et faire un appro direct, mais le
          backend force sa boutique assignée (scope boutique). */}
      <Route element={<ProtectedLayout roles={['admin', 'proprietaire', 'gerant_vendeur', 'consultation']} />}>
        <Route path="fournisseurs" element={<Fournisseurs />} />
        <Route path="bons-commande" element={<BonsCommande />} />
        {/* Journal des ventes : accessible aussi au gérant/vendeur, qui n'y
            voit QUE ses propres opérations (filtre appliqué côté serveur). */}
        <Route path="journal-ventes" element={<JournalVentes />} />
      </Route>

      {/* Finances + gestion — admin/propriétaire ET consultation (lecture seule).
          Les écritures restent bloquées (backend + UI). Le gérant n'y accède pas. */}
      <Route element={<ProtectedLayout roles={['admin', 'proprietaire', 'consultation']} />}>
        <Route path="caisse" element={<Caisse />} />
        <Route path="rapports" element={<Rapports />} />
        <Route path="prix" element={<PrixPromotions />} />
        <Route path="taxes" element={<Taxes />} />
      </Route>

      {/* Réglages / administration — admin + propriétaire uniquement
          (le profil consultation n'y a PAS accès). */}
      <Route element={<ProtectedLayout roles={['admin', 'proprietaire']} />}>
        <Route path="parametres" element={<Parametres />} />
        <Route path="activite"   element={<ActiviteCompte />} />
        <Route path="abonnement" element={<MonAbonnement />} />
        {/* /mon-entreprise → redirige vers Paramètres (l'écran y est intégré) */}
        <Route path="mon-entreprise" element={<Navigate to="/parametres" replace />} />
      </Route>

      {/* ─── Branche Super Admin (auth séparée) ─────────────────── */}
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />
      <Route path="/superadmin/reset-password" element={<SuperAdminResetPassword />} />
      <Route path="/superadmin" element={<ProtectedSuperAdminLayout />}>
        <Route index element={<Navigate to="/superadmin/commerces" replace />} />
        <Route path="commerces" element={<SuperAdminCommerces />} />
        <Route path="commerces/:id" element={<SuperAdminCommerceDetail />} />
        <Route path="presence" element={<SuperAdminPresence />} />
        <Route path="super-admins" element={<SuperAdminGestionSA />} />
        <Route path="plans" element={<SuperAdminPlans />} />
        <Route path="sauvegardes" element={<SuperAdminSauvegardes />} />
        <Route path="audit-logs"  element={<SuperAdminAuditLogs />} />
        <Route path="profil"      element={<SuperAdminMonProfil />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SuperAdminAuthProvider>
          <AppRoutes />
          <PWAInstaller />
          <NouveautesAuto />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#1f2937',
                color: '#fff',
                border: '1px solid #374151',
              },
              success: { iconTheme: { primary: '#eab308', secondary: '#000' } },
            }}
          />
        </SuperAdminAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
